import { AugmentedStore } from 'ia-react-core/~/interfaces/AugmentedStore';

import { ReducerInfo } from 'ia-react-core/~/interfaces/ReducerInfo';

import { AugmentedAction, IdentifiedAction } from 'ia-react-core/~/interfaces/AugmentedAction';
import { CoreAction } from 'ia-react-core/~/interfaces/CoreAction';
import keys from 'ia-react-core/~/utils/accessors/keys';
import Schema from 'ia-react-core/~/schemas/Schema';

import REDUX from '../../../constants/REDUX';
import get from '../../../utils/accessors/get';
import exposeVirtuals from './exposeVirtuals';
import cleanReducers from './cleanReducers';
import initializeReducers from './initializeReducers';
import reduceNamespace from './reduceNamespace';
import stateChangesMap from '../../../utils/redux/#stateChangesMap';

export default function createRootReducer<State extends object>(store: AugmentedStore<State>, schema: Schema) {
  return (state: State = {} as State, action = {} as CoreAction): State => {
    const changes: string[] = [];

    const cleanedState = cleanReducers<State>(store, state, action, changes);

    const initializedState = initializeReducers<State>(store, cleanedState, action, changes);

    const { unnamespacedReducers, namespacedReducers } = store.injectedReducers;

    const currentReducerStores = [
      unnamespacedReducers,
    ];

    const actionNameSpace = (action as AugmentedAction)[REDUX.ACTION_NAMESPACE];

    if (actionNameSpace && namespacedReducers[actionNameSpace]) {
      currentReducerStores.push(namespacedReducers[actionNameSpace].unidentified);

      const actionIdentifier = (action as AugmentedAction)[REDUX.ACTION_IDENTIFIER];
      if (actionIdentifier) {
        const actionIdentifierValue = (action as AugmentedAction)[actionIdentifier];

        const type = actionNameSpace.split('/').pop().toLowerCase();

        const fixedActionIdentifierValue = actionIdentifier === 'name' ?
          schema.decorateComponent(type, { name: actionIdentifierValue }).name :
          actionIdentifierValue;

        // Fix action
        if (actionIdentifier === 'name') {
          (action as IdentifiedAction<'name'>).name = fixedActionIdentifierValue;
          action.type = action.type.replace(new RegExp(`/${actionIdentifierValue}$`), `/${fixedActionIdentifierValue}`);
        }

        const identifiedReducers = get(namespacedReducers, [
          ...`${actionNameSpace}.identified.${actionIdentifier}`.split('.'),
          fixedActionIdentifierValue,
        ]) as ReducerInfo<any>[];

        if (identifiedReducers) {
          currentReducerStores.push(identifiedReducers);
        }
      } else {
        // If it is a namespaced action but without identifiers, every action of this namespace have to be triggered
        const identifiedStore = namespacedReducers[actionNameSpace].identified;
        keys(identifiedStore).forEach((identifierName) => {
          keys(identifiedStore[identifierName]).forEach((identifierValue) => {
            currentReducerStores.push(
              identifiedStore[identifierName][identifierValue],
            );
          });
        });
      }
    }

    const updatedState = currentReducerStores.reduce((currentState, currentReducerStore) => (
      reduceNamespace(currentState, action, currentReducerStore, changes)
    ), initializedState);

    const virtualizedState = exposeVirtuals(updatedState, store);

    stateChangesMap.set(virtualizedState, changes);

    return virtualizedState as State;
  };
}
